import AsyncStorage from '@react-native-async-storage/async-storage';
import type { InboxProviderId } from '@/services/inboxSync';

const PENDING_KEY = '@tripflow_inbox_oauth_pending';
const SUCCESS_KEY = '@tripflow_inbox_oauth_success';

type StoredOauthEvent = {
  provider: InboxProviderId;
  at: number;
};

export async function setInboxOauthPending(provider: InboxProviderId): Promise<void> {
  const payload: StoredOauthEvent = { provider, at: Date.now() };
  await AsyncStorage.setItem(PENDING_KEY, JSON.stringify(payload));
}

export async function setInboxOauthSuccess(provider: InboxProviderId): Promise<void> {
  const payload: StoredOauthEvent = { provider, at: Date.now() };
  await AsyncStorage.setItem(SUCCESS_KEY, JSON.stringify(payload));
}

export async function getInboxOauthSuccess(maxAgeMs = 5 * 60 * 1000): Promise<StoredOauthEvent | null> {
  try {
    const raw = await AsyncStorage.getItem(SUCCESS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredOauthEvent;
    if (!parsed?.provider || !parsed?.at) return null;
    if (Date.now() - parsed.at > maxAgeMs) return null;
    return parsed;
  } catch {
    return null;
  }
}

export async function getInboxOauthPending(maxAgeMs = 10 * 60 * 1000): Promise<StoredOauthEvent | null> {
  try {
    const raw = await AsyncStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredOauthEvent;
    if (!parsed?.provider || !parsed?.at) return null;
    if (Date.now() - parsed.at > maxAgeMs) return null;
    return parsed;
  } catch {
    return null;
  }
}

export async function clearInboxOauthMarkers(): Promise<void> {
  await Promise.all([
    AsyncStorage.removeItem(PENDING_KEY),
    AsyncStorage.removeItem(SUCCESS_KEY),
  ]);
}

export async function clearInboxOauthPending(): Promise<void> {
  await AsyncStorage.removeItem(PENDING_KEY);
}

export async function clearInboxOauthSuccess(): Promise<void> {
  await AsyncStorage.removeItem(SUCCESS_KEY);
}

export function getAllowedOauthOrigins(projectId?: string): string[] {
  if (typeof window === 'undefined') return [];

  const resolvedProjectId =
    projectId ||
    process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID ||
    '';
  const origins: string[] = [
    window.location.origin,
    'https://us-central1-tripflow1-fb52f.cloudfunctions.net',
    'https://tripflow1-fb52f.cloudfunctions.net',
  ];

  if (resolvedProjectId) {
    origins.push(`https://us-central1-${resolvedProjectId}.cloudfunctions.net`);
    origins.push(`https://${resolvedProjectId}.cloudfunctions.net`);
  }

  return Array.from(new Set(origins));
}

function normalizeOrigin(origin: string): string | null {
  try {
    return new URL(origin).origin;
  } catch {
    return null;
  }
}

export function isTrustedOauthOrigin(eventOrigin: string, allowedOrigins: string[]): boolean {
  const normalizedEventOrigin = normalizeOrigin(eventOrigin);
  if (!normalizedEventOrigin) return false;

  return allowedOrigins.some((origin) => {
    const normalizedAllowedOrigin = normalizeOrigin(origin);
    return normalizedAllowedOrigin != null && normalizedAllowedOrigin === normalizedEventOrigin;
  });
}
